import PageLoader from '@components/PageLoader'
import { countLabel, pluralize } from '@/utils/plural'
import { useAdminTrips } from '@hooks/useAdminTrips'
import React, { useEffect, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { AdminHeading } from '../AdminLayout.styles'
import { useAdminFilter } from '../AdminFilterContext'
import ListStats from '../ListStats'
import {
	DeletedBadge,
	EmptyState,
	ErrorState,
	InternalBadge,
	Muted,
	OwnerEmail,
	OwnerName,
	PageButton,
	PageStatus,
	Pagination,
	ResultCount,
	SearchInput,
	Table,
	TableWrapper,
	Td,
	Th,
	Toolbar,
	Tr,
} from './TripsPage.styles'

const PAGE_SIZE = 25

const MONTHS = [
	'Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec',
]

/** `YYYYMMDD` (date-only) → "MMM D, YYYY", or a dash placeholder when unset. */
const formatStartDate = (yyyymmdd: string | null): string => {
	if (!yyyymmdd || yyyymmdd.length !== 8) return '—'
	const year = yyyymmdd.slice(0, 4)
	const month = Number(yyyymmdd.slice(4, 6)) - 1
	const day = Number(yyyymmdd.slice(6, 8))
	if (month < 0 || month > 11) return '—'
	return `${MONTHS[month]} ${day}, ${year}`
}

/** Unix seconds (creation instant) → "MMM D, YYYY" in UTC. */
const formatCreated = (unixSeconds: number): string => {
	const d = new Date(unixSeconds * 1000)
	return `${MONTHS[d.getUTCMonth()]} ${d.getUTCDate()}, ${d.getUTCFullYear()}`
}

/**
 * Admin Trips list: every trip across the product, searchable by name or owner,
 * server-paginated, and scoped to the global creation-date range. Soft-deleted
 * trips are included and visibly flagged (admin-scoped per ADR 0001).
 */
const TripsPage: React.FC = () => {
	const navigate = useNavigate()
	const { from, to } = useAdminFilter()
	const [page, setPage] = useState(1)
	const [searchInput, setSearchInput] = useState('')
	const [q, setQ] = useState('')

	// Debounce the search box, and reset to page 1 whenever the term settles.
	useEffect(() => {
		const handle = setTimeout(() => {
			setQ(searchInput.trim())
			setPage(1)
		}, 300)
		return () => clearTimeout(handle)
	}, [searchInput])

	// A new date range can shrink the result set below the current page; restart.
	useEffect(() => {
		setPage(1)
	}, [from, to])

	const { data, isLoading, isError } = useAdminTrips({ page, pageSize: PAGE_SIZE, q })

	const total = data?.total ?? 0
	const pageCount = data?.totalPages ?? 1

	return (
		<>
			<AdminHeading>Trips</AdminHeading>

			<Toolbar>
				<SearchInput
					type="search"
					placeholder="Search by trip name or owner…"
					aria-label="Search trips"
					value={searchInput}
					onChange={e => setSearchInput(e.target.value)}
				/>
				{!isLoading && !isError && (
					<ResultCount>
						{total.toLocaleString()} {pluralize(total, 'trip')}
					</ResultCount>
				)}
			</Toolbar>

			{isLoading ? (
				<PageLoader label="Loading trips…" />
			) : isError || !data ? (
				<ErrorState>Could not load trips. Please try again.</ErrorState>
			) : (
				<>
					<ListStats noun="trips" total={data.totalAllTime} newInRange={data.total} />

					<TableWrapper>
						<Table>
							<thead>
								<tr>
									<Th>Trip</Th>
									<Th>Owner</Th>
									<Th>Members</Th>
									<Th>Events</Th>
									<Th>Ideas</Th>
									<Th>Dates</Th>
									<Th>Created</Th>
									<Th>Expenses</Th>
								</tr>
							</thead>
							<tbody>
								{data.trips.length === 0 ? (
									<tr>
										<Td colSpan={8}>
											<EmptyState>No trips match the current filters.</EmptyState>
										</Td>
									</tr>
								) : (
									data.trips.map(trip => (
										<Tr
											key={trip.id}
											$deleted={trip.isDeleted}
											onClick={() => navigate(`/admin/trips/${trip.id}`)}
										>
											<Td>
												{trip.name || <Muted>Untitled</Muted>}
												{trip.isInternal && <InternalBadge>Internal</InternalBadge>}
												{trip.isDeleted && <DeletedBadge>Deleted</DeletedBadge>}
											</Td>
											<Td>
												<OwnerName>{trip.ownerName || <Muted>Unknown</Muted>}</OwnerName>
												<OwnerEmail>{trip.ownerEmail}</OwnerEmail>
											</Td>
											<Td>{trip.memberCount.toLocaleString()}</Td>
											<Td>{trip.eventCount.toLocaleString()}</Td>
											<Td>{trip.ideaCount.toLocaleString()}</Td>
											<Td>
												{formatStartDate(trip.startDate)}
												{trip.dayCount > 0 && (
													<Muted>
														{' · '}
														{countLabel(trip.dayCount, 'day')}
													</Muted>
												)}
											</Td>
											<Td>{formatCreated(trip.createdAt)}</Td>
											<Td>{trip.expenseCount.toLocaleString()}</Td>
										</Tr>
									))
								)}
							</tbody>
						</Table>
					</TableWrapper>

					<Pagination>
						<PageStatus>
							Page {page} of {pageCount}
						</PageStatus>
						<PageButton disabled={page <= 1} onClick={() => setPage(p => Math.max(1, p - 1))}>
							← Prev
						</PageButton>
						<PageButton
							disabled={page >= pageCount}
							onClick={() => setPage(p => Math.min(pageCount, p + 1))}
						>
							Next →
						</PageButton>
					</Pagination>
				</>
			)}
		</>
	)
}

export default TripsPage
